// skipcq: JS-W1028, JS-0128
import React from 'react';
import PropTypes from 'prop-types';
import {
  CardContent,
  CardActions,
  Typography,
  CircularProgress,
  Box,
  Button,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import useSystemStats from '../../hooks/useSystemStats';
import useReviewsTrend from '../../hooks/useReviewsTrend';
import DashboardSurfaceCard from './DashboardSurfaceCard';
import ReviewReactionStatsBlock from './ReviewReactionStatsBlock';
import Sparkline from '../widgets/Sparkline';

function calculateReviewChange(values) {
  const firstValue = values[0] || 0;
  const lastValue = values[values.length - 1] || 0;
  const countChange = lastValue - firstValue;
  const percentChange = firstValue > 0 ? (countChange / firstValue) * 100 : 0;
  return { countChange, percentChange };
}

function reviewTrendColor(change) {
  if (change > 0) {
    return 'success.main';
  }
  if (change < 0) {
    return 'error.main';
  }
  return 'text.secondary';
}

function ReviewTrendGlyph({ change, fontSize }) {
  const iconSx = {
    color: reviewTrendColor(change),
    fontSize,
    mr: 0.5,
  };
  if (change > 0) {
    return <TrendingUp sx={iconSx} />;
  }
  if (change < 0) {
    return <TrendingDown sx={iconSx} />;
  }
  return <TrendingFlat sx={iconSx} />;
}

ReviewTrendGlyph.propTypes = {
  change: PropTypes.number.isRequired,
  fontSize: PropTypes.number.isRequired,
};

function ReviewsCard() {
  const { systemStats, loading, error } = useSystemStats();
  const { trendData, loading: trendLoading } = useReviewsTrend();

  // Extract latest totals
  const hospitalityTotal =
    systemStats?.metrics?.totalHospitalityReviewsCount || 0;
  const beerTotal = systemStats?.metrics?.totalBeerReviewsCount || 0;

  // Extract trend values for sparklines
  const hospitalityValues = trendData.map((item) => item.hospitalityReviewsCount);
  const beerValues = trendData.map((item) => item.beerReviewsCount);

  const hospitalityChange = calculateReviewChange(hospitalityValues);
  const beerChange = calculateReviewChange(beerValues);

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Reviews
        </Typography>
        <Typography variant="body2" paragraph sx={{
          color: "text.secondary"
        }}>
          Hospitality and beer review activity
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            Error loading stats
          </Typography>
        ) : (
          <>
            {/* Hospitality section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
              <Typography variant="h4" component="div" color="primary" sx={{ mr: 2 }}>
                {hospitalityTotal.toLocaleString()}
              </Typography>
              {!trendLoading && hospitalityValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={hospitalityValues}
                      width="100%"
                      height={24}
                      color="primary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <ReviewTrendGlyph
                      change={hospitalityChange.countChange}
                      fontSize={16}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: reviewTrendColor(hospitalityChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {hospitalityChange.countChange > 0 ? '+' : ''}
                        {hospitalityChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: reviewTrendColor(hospitalityChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({hospitalityChange.percentChange > 0 ? '+' : ''}
                        {hospitalityChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Hospitality reviews
            </Typography>

            {/* Beer section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mt: 1
              }}>
              <Typography variant="h5" component="div" color="secondary" sx={{ mr: 2 }}>
                {beerTotal.toLocaleString()}
              </Typography>
              {!trendLoading && beerValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={beerValues}
                      width="100%"
                      height={20}
                      color="secondary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <ReviewTrendGlyph
                      change={beerChange.countChange}
                      fontSize={14}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: reviewTrendColor(beerChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {beerChange.countChange > 0 ? '+' : ''}
                        {beerChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: reviewTrendColor(beerChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({beerChange.percentChange > 0 ? '+' : ''}
                        {beerChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Beer reviews
            </Typography>
            <ReviewReactionStatsBlock
              title="Beer review reactions"
              metricsPrefix="beer"
              metrics={systemStats?.metrics}
            />
            <ReviewReactionStatsBlock
              title="Brewery review reactions"
              metricsPrefix="hospitality"
              metrics={systemStats?.metrics}
            />
          </>
        )}
      </CardContent>
      <CardActions>
        <Button variant="outlined" component={Link} to="/reviews" fullWidth>
          View Reviews
        </Button>
      </CardActions>
    </DashboardSurfaceCard>
  );
}

export default ReviewsCard;


